import DashedOutlineWithInnerRectIcon from '@src/svgs/DashedOutlineWithInnerRectIcon';
import DashedRectIcon from '@src/svgs/DashedRectIcon';
import { type FC, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { useOutsideAlerter } from '../transcript/useClickOutside';
import {
  AspectDropDown,
  AspectItem,
  AspectItemContent,
  AspectRatioLogo,
  AspectRow,
  CaretDownIcon,
  DropdownIcon,
  HorizontalAspectIcon,
  SelectedAspectRatio,
  SquareAspectIcon,
  VerticalAspectIcon,
} from './AspectRatioUiPrimitives';

const ASPECT_OPTIONS = [
  {
    value: 'custom',
    buttonLabel: 'Custom',
    menuLabel: 'Custom',
    icon: 'custom',
  },
  {
    value: 'original',
    buttonLabel: 'Original',
    menuLabel: 'Original',
    icon: 'original',
  },
  {
    value: '16:9',
    buttonLabel: '16:9',
    menuLabel: '16:9 (Wide)',
    icon: 'wide',
  },
  {
    value: '9:16',
    buttonLabel: '9:16',
    menuLabel: '9:16 (Vertical)',
    icon: 'vertical',
  },
  {
    value: '1:1',
    buttonLabel: '1:1',
    menuLabel: '1:1 (Square)',
    icon: 'square',
  },
];

type AspectOption = (typeof ASPECT_OPTIONS)[number];

type IconVariant = AspectOption['icon'];

type AspectValue = AspectOption['value'];

export type { AspectValue };

export interface ReframeAspectDropdownProps {
  value: AspectValue;
  onChange: (value: AspectValue) => void;
}

export const ReframeAspectDropdown: FC<ReframeAspectDropdownProps> = ({
  value,
  onChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(wrapperRef, () => setIsOpen(false));

  const selectedOption = useMemo<AspectOption>(() => {
    return (
      ASPECT_OPTIONS.find(option => option.value === value) || ASPECT_OPTIONS[0]
    );
  }, [value]);

  const renderIcon = (variant: IconVariant, isSelected: boolean) => {
    switch (variant) {
      case 'custom':
        return (
          <AspectRatioLogo>
            <DashedOutlineWithInnerRectIcon
              width="14"
              height="14"
              fillColor={
                isSelected ? 'var(--primary-hover)' : 'var(--text-secondary)'
              }
            />
          </AspectRatioLogo>
        );
      case 'original':
        return (
          <AspectRatioLogo>
            <DashedRectIcon
              width="14"
              height="14"
              fillColor={
                isSelected ? 'var(--primary-hover)' : 'var(--text-secondary)'
              }
            />
          </AspectRatioLogo>
        );
      case 'vertical':
        return (
          <AspectRatioLogo>
            <VerticalAspectIcon isSelected={isSelected} />
          </AspectRatioLogo>
        );
      case 'square':
        return (
          <AspectRatioLogo>
            <SquareAspectIcon isSelected={isSelected} />
          </AspectRatioLogo>
        );
      case 'wide':
      default:
        return (
          <AspectRatioLogo>
            <HorizontalAspectIcon isSelected={isSelected} />
          </AspectRatioLogo>
        );
    }
  };

  return (
    <Wrapper ref={wrapperRef}>
      <Label>Aspect ratio:</Label>
      <RelativeWrapper>
        <AspectItem onClick={() => setIsOpen(prev => !prev)}>
          <AspectItemContent>
            {renderIcon(selectedOption.icon, false)}
            <SelectedAspectRatio>
              {selectedOption.buttonLabel}
            </SelectedAspectRatio>
            <DropdownIcon isOpen={isOpen} aria-hidden="true">
              <CaretDownIcon />
            </DropdownIcon>
          </AspectItemContent>
        </AspectItem>
        {isOpen && (
          <AspectDropDown>
            {ASPECT_OPTIONS.map(option => {
              const isSelected = option.value === value;
              return (
                <AspectRow
                  key={option.value}
                  isSelected={isSelected}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {renderIcon(option.icon, isSelected)}
                  <MenuLabel>{option.menuLabel}</MenuLabel>
                </AspectRow>
              );
            })}
          </AspectDropDown>
        )}
      </RelativeWrapper>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  min-width: 160px;
  flex-shrink: 0;
`;

const RelativeWrapper = styled.div`
  position: relative;
`;

const Label = styled.span`
  color: var(--text-secondary);
  text-align: center;
  font-family: var(--font-primary);
  font-size: 16px;
  font-style: normal;
  font-weight: var(--font-weight-bold);
  line-height: 120%; /* 19.2px */
`;

const MenuLabel = styled.span`
  flex: 1;
`;
